import { memo, useMemo } from 'react';
import { useMedia } from 'react-use';
import { ProductList } from '~/features/products/components/ProductList/ProductList';
import { useProductViewTracking } from '~/features/tracking/hooks/useProductViewTracking';
import { Button } from '~/shared/components/Button/Button';
import Link from '~/shared/components/Link/Link';
import { LoadingOverlay } from '~/shared/components/Loading/LoadingOverlay';
import { useRouteTransition } from '~/shared/utils/route-transition/hooks/useRouteTransition';
import { useTranslation } from '~/shared/utils/translation/hooks/useTranslation';
import FilterHeader from '~/templates/blocks/components/M170ProductFilter/components/FilterHeader';
import { smCondition } from '~/theme/lib/breakpoints';
import { ModuleContainer } from '../ModuleContainer/ModuleContainer';
import { useProductFilterData } from './hooks/useProductFilterData';
import { M170ProductFilterProps } from './models/M170ProductFilter';
import { StyledCaption, StyledPaginationWrapper, StyledProductListWrapper } from './components/styled';

export const M170ProductFilter = memo((props: M170ProductFilterProps) => {
	const { initialData, listId, listName } = props;
	const { translate } = useTranslation();
	const { loading: pageLoading } = useRouteTransition();
	const { filter: filterData, pagination, filterExpandState } = useProductFilterData(initialData.filter, initialData.id);

	const { facets, selectedFacets, isLoading: filterLoading } = filterData;

	useProductViewTracking(pagination.items, {
		pageSize: pagination.pageSize,
		listName: listName,
		listId: listId,
		enable: true,
	});

	const selectableFacetValues = useMemo(() => {
		return filterExpandState.expandedOption === 'facet' && filterExpandState.expandedOptionId ? facets?.find((facet) => facet.id === filterExpandState.expandedOptionId)?.values || [] : [];
	}, [facets, filterExpandState.expandedOption, filterExpandState.expandedOptionId]);

	const isBreakpointAboveMobile = useMedia(smCondition, false);
	const showSelectedFacets = isBreakpointAboveMobile && selectedFacets.length > 0;
	const showFacetsList = facets.length <= 5;

	return (
		<LoadingOverlay isLoading={filterLoading || pageLoading}>
			<ModuleContainer
				isSticky
				id={initialData.id}
				anchorId={initialData?.anchorId?.toLocaleLowerCase()}
			>
				<FilterHeader
					filterData={filterData}
					filterExpandState={filterExpandState}
					selectableFacetValues={selectableFacetValues}
					showFacetsList={showFacetsList}
					showSelectedFacets={showSelectedFacets}
					productsCount={pagination.total}
					translate={translate}
				/>

				<StyledProductListWrapper noMargin={showSelectedFacets}>
					<ProductList
						items={pagination.items}
						listName={listName}
						listId={listId}
					/>
				</StyledProductListWrapper>

				<StyledPaginationWrapper>
					{pagination.nextPageItemCount > 0 ? (
						<Button
							as={Link}
							href={pagination.nextPageLink}
							scroll={false}
							replace
						>
							{translate('pagination.load-more-button', {
								quantity: pagination.nextPageItemCount,
							})}
						</Button>
					) : null}

					<StyledCaption>
						{translate(
							'pagination.load-more-caption',
							{
								count: pagination.productsCount,
								total: pagination.total,
							},
							'total',
						)}
					</StyledCaption>
				</StyledPaginationWrapper>
			</ModuleContainer>
		</LoadingOverlay>
	);
});
